import React, { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { RefreshCw } from 'lucide-react';

export const CCTVSettings = ({ settings, onSave }) => {
  const [localSettings, setLocalSettings] = useState(settings);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader><CardTitle>Video Settings</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div><p className="font-medium">Video Stream Quality</p><p className="text-sm text-muted-foreground">Default quality for live streams</p></div>
            <Select value={localSettings.video_stream_quality || 'auto'} onValueChange={(v) => setLocalSettings({...localSettings, video_stream_quality: v})}>
              <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="auto">Auto</SelectItem><SelectItem value="hd">HD</SelectItem><SelectItem value="sd">SD</SelectItem></SelectContent>
            </Select>
          </div>
          <div className="flex items-center justify-between">
            <div><p className="font-medium">Hard Decoding</p><p className="text-sm text-muted-foreground">Use hardware acceleration</p></div>
            <Switch checked={localSettings.hard_decoding ?? true} onCheckedChange={(c) => setLocalSettings({...localSettings, hard_decoding: c})} />
          </div>
          <div className="flex items-center justify-between">
            <div><p className="font-medium">Auto Highlight</p><p className="text-sm text-muted-foreground">Automatically highlight motion events</p></div>
            <Switch checked={localSettings.auto_highlight || false} onCheckedChange={(c) => setLocalSettings({...localSettings, auto_highlight: c})} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Storage & Retention</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div><p className="font-medium">Retention Period</p><p className="text-sm text-muted-foreground">Days to keep recordings</p></div>
            <Select value={String(localSettings.retention_days || 30)} onValueChange={(v) => setLocalSettings({...localSettings, retention_days: parseInt(v)})}>
              <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="7">7 days</SelectItem><SelectItem value="14">14 days</SelectItem><SelectItem value="30">30 days</SelectItem><SelectItem value="60">60 days</SelectItem><SelectItem value="90">90 days</SelectItem></SelectContent>
            </Select>
          </div>
          <div className="flex items-center justify-between">
            <div><p className="font-medium">Recording Mode</p><p className="text-sm text-muted-foreground">Default recording trigger</p></div>
            <Select value={localSettings.default_recording_mode || 'continuous'} onValueChange={(v) => setLocalSettings({...localSettings, default_recording_mode: v})}>
              <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="continuous">Continuous</SelectItem><SelectItem value="motion">Motion Only</SelectItem><SelectItem value="scheduled">Scheduled</SelectItem></SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Alerts & Notifications</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div><p className="font-medium">Enable Notifications</p><p className="text-sm text-muted-foreground">Receive alerts for device issues</p></div>
            <Switch checked={localSettings.notification_enabled ?? true} onCheckedChange={(c) => setLocalSettings({...localSettings, notification_enabled: c})} />
          </div>
          <div className="flex items-center justify-between">
            <div><p className="font-medium">Offline Alert Threshold</p><p className="text-sm text-muted-foreground">Minutes before marking offline</p></div>
            <Select value={String(localSettings.offline_alert_threshold_minutes || 5)} onValueChange={(v) => setLocalSettings({...localSettings, offline_alert_threshold_minutes: parseInt(v)})}>
              <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="1">1 min</SelectItem><SelectItem value="5">5 min</SelectItem><SelectItem value="10">10 min</SelectItem><SelectItem value="30">30 min</SelectItem></SelectContent>
            </Select>
          </div>
          <div><Label>Notification Email</Label><Input value={localSettings.notification_email || ''} onChange={(e) => setLocalSettings({...localSettings, notification_email: e.target.value})} placeholder="alerts@example.com" /></div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>System</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div><p className="font-medium">Device Upgrade Method</p><p className="text-sm text-muted-foreground">How to apply firmware updates</p></div>
            <Select value={localSettings.device_upgrade_method || 'manual'} onValueChange={(v) => setLocalSettings({...localSettings, device_upgrade_method: v})}>
              <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="manual">Manual</SelectItem><SelectItem value="auto">Automatic</SelectItem></SelectContent>
            </Select>
          </div>
          <Button onClick={() => toast.info('Checking for updates...')}><RefreshCw className="h-4 w-4 mr-2" />Check for Updates</Button>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={() => onSave(localSettings)}>Save Settings</Button>
      </div>
    </div>
  );
};
